import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Minus, Plus, Ruler, Truck, ArrowLeft } from "lucide-react";
import { getProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const SIZE_GUIDE = {
  "distressed-layered-tee": [["XS", "34-36", "27"], ["S", "37-39", "28"], ["M", "40-42", "29"], ["L", "43-45", "30"], ["XL", "46-48", "31"]],
  "washed-black-denim-jorts": [["28", "28", "20"], ["30", "30", "20.5"], ["32", "32", "21"], ["34", "34", "21.5"], ["36", "36", "22"]],
};

export default function Product() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState(false);
  const { addItem } = useCart();

  useEffect(() => { setP(null); getProduct(id).then((d) => { setP(d); setActive(0); setSize(""); setQty(1); }).catch(() => {}); }, [id]);

  if (!p) return <div className="min-h-[60vh] flex items-center justify-center font-mono text-graytx text-sm">Loading…</div>;

  const add = () => {
    if (!size) { toast.error("Select a size first"); return; }
    addItem(p, size, qty);
    toast.success(`${p.name} (${size}) added to bag`);
  };

  const guide = SIZE_GUIDE[p.id] || [];
  const cols = p.id === "washed-black-denim-jorts" ? ["Size", "Waist (in)", "Length (in)"] : ["Size", "Chest (in)", "Length (in)"];

  return (
    <div data-testid="product-page">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-8 pt-6">
        <Link to="/shop" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-graytx hover:text-cream transition-colors duration-200" data-testid="back-to-shop">
          <ArrowLeft size={14} /> Back to shop
        </Link>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-8 py-8 grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* GALLERY */}
        <div>
          <Dialog open={zoom} onOpenChange={setZoom}>
            <DialogTrigger asChild>
              <div className="aspect-[4/5] overflow-hidden bg-charcoal cursor-zoom-in" data-testid="product-main-image">
                <img src={p.images[active]} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </DialogTrigger>
            <DialogContent className="max-w-4xl bg-bg border-line p-0">
              <img src={p.images[active]} alt={p.name} className="w-full h-full object-contain" data-testid="product-zoom-image" />
            </DialogContent>
          </Dialog>
          <div className="grid grid-cols-3 gap-3 mt-3">
            {p.images.map((img, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                className={`aspect-square overflow-hidden bg-charcoal border ${active === i ? "border-cream" : "border-transparent"} transition-colors duration-200`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* DETAILS (sticky) */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="flex gap-2 mb-4">
            {p.preorder && <span className="bg-charcoal font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1.5" data-testid="preorder-badge">Pre-order</span>}
            {p.low_stock_sizes?.length > 0 && <span className="bg-cs-red font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1.5" data-testid="lowstock-badge">Low stock on {p.low_stock_sizes.join(", ")}</span>}
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl leading-[0.95] uppercase">{p.name}</h1>
          <p className="text-graytx mt-3">{p.tagline}</p>
          <p className="font-mono text-2xl mt-5" data-testid="product-price">${p.price.toFixed(0)}</p>

          <div className="flex items-center gap-2 mt-5 font-mono text-[11px] uppercase tracking-widest text-cs-green" data-testid="ship-estimate">
            <Truck size={14} /> {p.ship_estimate}
          </div>

          {/* Sizes */}
          <div className="mt-8">
            <div className="flex justify-between items-center mb-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Size</span>
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-graytx hover:text-cream transition-colors duration-200" data-testid="size-guide-btn">
                    <Ruler size={13} /> Size guide
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-bg border-line text-cream">
                  <DialogHeader><DialogTitle className="font-display uppercase">Size Guide</DialogTitle></DialogHeader>
                  <table className="w-full font-mono text-xs mt-2">
                    <thead><tr className="text-graytx border-b border-line">{cols.map((c) => <th key={c} className="text-left py-2">{c}</th>)}</tr></thead>
                    <tbody>{guide.map((r) => <tr key={r[0]} className="border-b border-line">{r.map((v, i) => <td key={i} className="py-2">{v}</td>)}</tr>)}</tbody>
                  </table>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex flex-wrap gap-2">
              {p.sizes.map((s) => {
                const low = p.low_stock_sizes?.includes(s);
                return (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`min-w-[52px] h-11 px-3 border font-mono text-sm relative transition-colors duration-200 ${size === s ? "bg-cream text-bg border-cream" : "border-line hover:border-cream"}`}>
                    {s}
                    {low && <span className="absolute -top-1 -right-1 w-2 h-2 bg-cs-red rounded-full" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Qty + add */}
          <div className="flex gap-3 mt-6">
            <div className="flex items-center border border-line">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3.5 py-3.5" data-testid="qty-dec"><Minus size={14} /></button>
              <span className="px-4 tabular-nums" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3.5 py-3.5" data-testid="qty-inc"><Plus size={14} /></button>
            </div>
            <button onClick={add} data-testid="add-to-cart-btn"
              className="flex-1 bg-cream text-bg font-mono text-[12px] uppercase tracking-[0.2em] hover:bg-cs-yellow transition-colors duration-200">
              Pre-order — ${(p.price * qty).toFixed(0)}
            </button>
          </div>

          {/* Accordions */}
          <Accordion type="single" collapsible className="mt-8 border-t border-line" defaultValue="details">
            <AccordionItem value="details" className="border-line">
              <AccordionTrigger className="font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="acc-details">Product Details</AccordionTrigger>
              <AccordionContent className="text-graytx text-sm leading-relaxed">{p.details}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="material" className="border-line">
              <AccordionTrigger className="font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="acc-material">Material & Fit</AccordionTrigger>
              <AccordionContent className="text-graytx text-sm leading-relaxed">{p.material}<br /><br />{p.fit}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="care" className="border-line">
              <AccordionTrigger className="font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="acc-care">Care Instructions</AccordionTrigger>
              <AccordionContent className="text-graytx text-sm leading-relaxed">{p.care}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="preorder" className="border-line border-b">
              <AccordionTrigger className="font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="acc-preorder">Pre-order Terms</AccordionTrigger>
              <AccordionContent className="text-graytx text-sm leading-relaxed">
                This is a limited first-run pre-order. Your card is charged today to reserve your piece. Items ship {p.ship_estimate.toLowerCase()}. Free returns within 14 days of delivery.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Sticky mobile add-to-cart */}
      <motion.div initial={{ y: 100 }} animate={{ y: 0 }}
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-bg/95 backdrop-blur-md border-t border-line p-3 flex gap-3" data-testid="mobile-sticky-cart">
        <div className="flex items-center border border-line">
          <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3"><Minus size={14} /></button>
          <span className="px-3 tabular-nums">{qty}</span>
          <button onClick={() => setQty(qty + 1)} className="px-3 py-3"><Plus size={14} /></button>
        </div>
        <button onClick={add} data-testid="mobile-add-to-cart-btn" className="flex-1 bg-cream text-bg font-mono text-[12px] uppercase tracking-[0.2em] py-3">
          Pre-order — ${(p.price * qty).toFixed(0)}
        </button>
      </motion.div>
      <div className="lg:hidden h-20" />
    </div>
  );
}
